import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Clock, AlertCircle, CheckCircle2, Info, Loader } from 'lucide-react'
import styles from './StatusCard.module.css'

interface StatusCardProps {
  type: 'current' | 'activity' | 'insight' | 'concern' | 'waiting'
  title?: string
  message: string
  severity?: 'info' | 'warning' | 'error' | 'success'
  icon?: React.ReactNode
  timestamp?: string
  details?: string
  confidence?: number
  className?: string
}

const StatusCard: React.FC<StatusCardProps> = ({
  type,
  title,
  message,
  severity = 'info',
  icon,
  timestamp,
  details,
  confidence,
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasExpandableContent = !!details

  // Get icon based on type and severity
  const getIcon = () => {
    if (icon) return icon

    switch (type) {
      case 'current':
        return <Loader size={18} className={styles.spinningIcon} />
      case 'activity':
        return severity === 'success' ? <CheckCircle2 size={18} /> : <Clock size={18} />
      case 'insight':
        return <Info size={18} />
      case 'concern':
        return <AlertCircle size={18} />
      case 'waiting':
        return <Clock size={18} />
      default:
        return <Info size={18} />
    }
  }

  // Get card class based on type and severity
  const getCardClass = () => {
    const classes = [styles.statusCard, styles[type]]
    if (severity) {
      classes.push(styles[severity])
    }
    if (className) {
      classes.push(className)
    }
    return classes.join(' ')
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      
      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
      return date.toLocaleDateString()
    } catch {
      return timestamp
    }
  }

  return (
    <div className={getCardClass()}>
      <div className={styles.cardHeader} 
           style={{cursor: hasExpandableContent ? 'pointer' : 'default'}}
           onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
        >
        <div className={styles.iconContainer}>
          {getIcon()}
        </div>
        <div className={styles.content}>
          {title && <div className={styles.title}>{title}</div>}
          <div className={styles.message}>{message}</div>
          {timestamp && (
            <div className={styles.timestamp}>{formatTimestamp(timestamp)}</div>
          )}
          {confidence !== undefined && (
            <div className={styles.confidence}>
              <span className={styles.confidenceLabel}>Confidence:</span>
              <span className={styles.confidenceValue}>{Math.round(confidence * 100)}%</span>
            </div>
          )}
        </div>
        {hasExpandableContent && (
          <button
            className={styles.expandButton}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
      </div>
      {hasExpandableContent && isExpanded && (
        <div className={styles.details}>
          {details}
        </div>
      )}
    </div>
  )
}

export default StatusCard

